import Icon from "@/components/Icon";
import { DOMAIN } from "@/hooks/useCapabilities";
import { Path } from "@/router";
import React from "react";
import { Location, matchPath } from "react-router-dom";
import { NavigationLink, SidebarDivider } from ".";
import NavigationLinkWithFlyout from "./NavigationLinkWithFlyout";
import { NavLink } from "../types";

interface RenderNavLinksProps<P extends Path> {
  navLinks: NavLink<P>[];
  isLastGroup: boolean;
  location: Location;
  groupTitle?: string;
  isSidebarOpen: boolean;
  domains: string[];
  toggleSidebar?: () => void;
}

export const getMenuMatchDepth = (currentPath: string, linkTo: Path) => {
  let path = currentPath;

  while (path && path !== "/") {
    if (matchPath(linkTo, path)) {
      return path.split("/").filter(Boolean).length;
    }

    path = path.split("/").slice(0, -1).join("/");
  }

  return -1;
};

export const getNavLinkMatchDepth = (currentPath: string, linkTo: Path, matchPaths?: Path[]) => {
  const extraDepths = (matchPaths ?? []).map((path) => getMenuMatchDepth(currentPath, path));
  return Math.max(getMenuMatchDepth(currentPath, linkTo), ...extraDepths);
};

const renderNavLinks = <P extends Path>({
  navLinks,
  groupTitle,
  domains,
  location: { pathname },
  toggleSidebar
}: RenderNavLinksProps<P>) => {
  const permittedLinks = navLinks.filter(
    (navLink) => navLink.domain === DOMAIN.EVERYONE || domains.includes(navLink.domain)
  );

  return permittedLinks.length > 0 ? (
    <React.Fragment key={groupTitle}>
      {(() => {
        const linkMatchDepths = permittedLinks.map(({ link, externalLink, matchPaths }) =>
          externalLink ? -1 : getNavLinkMatchDepth(pathname, link.to, matchPaths)
        );
        const maxDepth = Math.max(...linkMatchDepths);

        return permittedLinks.map(
          ({ label, link, icon, trailingIcon, dividerBefore, externalLink, onClick, flyoutLinks }, key) => {
            const depth = linkMatchDepths[key];
            const active = !externalLink && depth > -1 && depth === maxDepth;
            const itemProps = {
              active,
              icon: <Icon name={icon} />,
              trailingIcon: trailingIcon ? <Icon name={trailingIcon} /> : undefined,
              toggleSidebar,
              onClick,
              label,
              link
            };

            return (
              <React.Fragment key={key}>
                {dividerBefore && <SidebarDivider />}
                {flyoutLinks?.length ? (
                  <NavigationLinkWithFlyout flyoutLinks={flyoutLinks} {...itemProps} />
                ) : (
                  <NavigationLink {...itemProps} />
                )}
              </React.Fragment>
            );
          }
        );
      })()}
    </React.Fragment>
  ) : null;
};

export default renderNavLinks;
